/**
 * <lp-collection-product-card> — CRO collection card ATC.
 * Uses window.Cart; cart-drawer opens on cart:change (add).
 */
export class LpCollectionProductCard extends HTMLElement {
  connectedCallback() {
    this._btn = this.querySelector('.lp-collection-card-v2__add');
    this._shop = this.querySelector('.lp-collection-card-v2__shop');

    if (this._btn) {
      this._soldOut = this._btn.disabled;
      this._labelEl = this.querySelector('.lp-collection-card-v2__add-label');
      this._label = this._labelEl?.textContent.trim() || 'Add';
      this._handleClick = this._handleClick.bind(this);
      this._handleKeydown = this._handleKeydown.bind(this);
      this._btn.addEventListener('click', this._handleClick);
      this._btn.addEventListener('keydown', this._handleKeydown);
    }

    if (this._shop) {
      this._handleShopKeydown = this._handleShopKeydown.bind(this);
      this._shop.addEventListener('keydown', this._handleShopKeydown);
    }
  }

  disconnectedCallback() {
    this._btn?.removeEventListener('click', this._handleClick);
    this._btn?.removeEventListener('keydown', this._handleKeydown);
    this._shop?.removeEventListener('keydown', this._handleShopKeydown);
    clearTimeout(this._errTimer);
  }

  _handleShopKeydown(event) {
    if (event.key !== ' ') return;
    event.preventDefault();
    this._shop?.click();
  }

  _handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    this._addToCart();
  }

  _handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this._addToCart();
  }

  _addToCart() {
    const variantId = this.dataset.variantId;
    if (!variantId || !window.Cart || !this._btn || this._btn.disabled || this._adding) return;

    const fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');

    this._clearError();
    this._adding = true;
    this._setLoading(true);

    window.Cart.add(fd, this)
      .catch((error) => this._showError(error.description || error.message))
      .finally(() => {
        this._adding = false;
        this._setLoading(false);
      });
  }

  _setLoading(loading) {
    if (!this._btn) return;
    this._btn.disabled = loading || this._soldOut;
    this._btn.setAttribute('aria-busy', String(loading));
    if (this._labelEl) {
      this._labelEl.textContent = loading ? '…' : this._label;
    }
  }

  _clearError() {
    const err = this.querySelector('.lp-collection-card-v2__add-error');
    if (err) err.textContent = '';
    clearTimeout(this._errTimer);
  }

  _showError(message) {
    let err = this.querySelector('.lp-collection-card-v2__add-error');
    if (!err) {
      err = document.createElement('p');
      err.className = 'lp-collection-card-v2__add-error';
      this.querySelector('.lp-collection-card-v2__media')?.appendChild(err);
    }
    err.textContent = message;
    this._errTimer = setTimeout(() => {
      err.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('lp-collection-product-card')) {
  customElements.define('lp-collection-product-card', LpCollectionProductCard);
}

/**
 * <lp-collection-grid> — CRO collection product grid + Load More (Figma 1:740).
 */
export class LpCollectionGrid extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this._grid = this.querySelector('[data-product-grid]');
    this._count = this.querySelector('[data-grid-count]');
    this._btn = this.querySelector('[data-load-more]');
    this._total = this._parseTotal();

    this._onLoadMore = this._onLoadMore.bind(this);
    this._btn?.addEventListener('click', this._onLoadMore);
  }

  disconnectedCallback() {
    this._btn?.removeEventListener('click', this._onLoadMore);
    delete this.dataset.initialized;
  }

  _parseTotal() {
    if (!this._count) return 0;
    const match = this._count.textContent.match(/of\s+([\d,]+)/i);
    return match ? Number(match[1].replace(/,/g, '')) : 0;
  }

  async _onLoadMore(event) {
    event.preventDefault();
    if (!this._btn || this._btn.getAttribute('aria-busy') === 'true') return;

    const url = this._btn.getAttribute('href');
    if (!url) return;

    this._btn.setAttribute('aria-busy', 'true');
    this._btn.textContent = 'Loading…';

    try {
      const response = await fetch(url);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const nextGrid = doc.querySelector('[data-product-grid]');
      const nextBtn = doc.querySelector('[data-load-more]');

      if (nextGrid && this._grid) {
        Array.from(nextGrid.children).forEach((child) => {
          this._grid.appendChild(child);
        });
      }

      if (this._count && this._grid) {
        const shown = this._grid.querySelectorAll('.lp-collection-grid-v2__item').length;
        const total = this._total || shown;
        this._count.textContent = `Showing ${shown} of ${total} items`;
      }

      if (nextBtn?.getAttribute('href')) {
        this._btn.setAttribute('href', nextBtn.getAttribute('href'));
        this._btn.textContent = 'Load More';
        this._btn.removeAttribute('aria-busy');
      } else {
        this._btn.hidden = true;
        this._btn.removeAttribute('href');
        this._btn.removeAttribute('aria-busy');
      }
    } catch (error) {
      console.error('Collection grid load more failed:', error);
      this._btn.textContent = 'Load More';
      this._btn.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lp-collection-grid')) {
  customElements.define('lp-collection-grid', LpCollectionGrid);
}
